import {
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';
import { useSeriesOptions } from '../../utils/useSeriesOptions';
import { InstrumentPill, ValuePill, FromIndicatorBadge } from './IndicatorPreviewFields';

export type CarryForwardFrom =
  | 'end_of_year'
  | 'beginning_of_year'
  | 'end_of_month'
  | 'beginning_of_month'
  | 'end_of_previous_month'
  | 'end_of_week'
  | 'beginning_of_week'
  | 'end_of_previous_week'
  | 'end_of_previous_year';

const FROM_OPTIONS: { value: CarryForwardFrom; label: string }[] = [
  { value: 'end_of_year', label: 'End of Year' },
  { value: 'beginning_of_year', label: 'Beginning of Year' },
  { value: 'end_of_previous_year', label: 'End of Previous Year' },
  { value: 'end_of_month', label: 'End of Month' },
  { value: 'beginning_of_month', label: 'Beginning of Month' },
  { value: 'end_of_previous_month', label: 'End of Previous Month' },
  { value: 'end_of_week', label: 'End of Week' },
  { value: 'beginning_of_week', label: 'Beginning of Week' },
  { value: 'end_of_previous_week', label: 'End of Previous Week' },
];

const FIELD_OPTIONS = [
  { value: 'close', label: 'Close' },
  { value: 'open', label: 'Open' },
  { value: 'high', label: 'High' },
  { value: 'low', label: 'Low' },
];

export type IndicatorEditorCarryForwardRef = {
  getConfig: () => {
    instrumentId: string;
    field: string;
    from: CarryForwardFrom;
  };
};

type Props = {
  config: {
    instrumentId: string;
    field?: string;
    from?: CarryForwardFrom;
  };
  onChange: (config: Props['config']) => void;
  editMode: boolean;
  indicatorId?: string;
};

const IndicatorEditorCarryForward = forwardRef<IndicatorEditorCarryForwardRef, Props>(
  ({ config, onChange, editMode, indicatorId }, ref) => {
    const allSeriesOptions = useSeriesOptions(indicatorId);

    const validSeriesOptions = allSeriesOptions.filter(series => {
      const fields = series.emittedFields;
      return ['Close', 'Open', 'High', 'Low'].some(
        f => f in fields && fields[f as keyof typeof fields] === 'Number'
      );
    });

    const hasSingleOption = validSeriesOptions.length === 1;

    useEffect(() => {
      if (hasSingleOption && config.instrumentId !== validSeriesOptions[0].id) {
        onChange({ ...config, instrumentId: validSeriesOptions[0].id });
      }
    }, [hasSingleOption, validSeriesOptions, config.instrumentId, onChange]);

    const selectedSeries = validSeriesOptions.find(s => s.id === config.instrumentId);
    const fields = selectedSeries?.emittedFields || {};
    const availableFields = FIELD_OPTIONS.filter(
      f => fields[f.label as keyof typeof fields] === 'Number'
    );
    const currentField = (config.field || 'close').toLowerCase();
    const currentFrom: CarryForwardFrom = (config.from as CarryForwardFrom) || 'beginning_of_month';

    useImperativeHandle(ref, () => ({
      getConfig: () => ({
        instrumentId: config.instrumentId,
        field: currentField,
        from: currentFrom,
      }),
    }));

    if (!editMode) {
      const label = selectedSeries?.label || '???';
      const fieldLabel = FIELD_OPTIONS.find(f => f.value === currentField)?.label ?? currentField;
      const fromLabel = FROM_OPTIONS.find(f => f.value === currentFrom)?.label ?? currentFrom;
      return (
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <InstrumentPill>{label}</InstrumentPill>
          <ValuePill label="Field" value={fieldLabel} />
          <ValuePill label="From" value={fromLabel} />
          {selectedSeries?.type === 'indicator' && <FromIndicatorBadge />}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-4 items-center">
        {!hasSingleOption && validSeriesOptions.length > 0 && (
          <label>
            Series:
            <select
              value={config.instrumentId}
              onChange={e => onChange({ ...config, instrumentId: e.target.value })}
              className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
            >
              {validSeriesOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label} {opt.type === 'indicator' ? ' (indicator)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {validSeriesOptions.length === 0 && (
          <span className="text-error text-sm">
            No series available with OHLC-style fields
          </span>
        )}

        {availableFields.length > 0 && (
          <>
            <label>
              Field:
              <select
                value={currentField}
                onChange={e => onChange({ ...config, field: e.target.value })}
                className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
              >
                {availableFields.map(f => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              From:
              <select
                value={currentFrom}
                onChange={e => onChange({ ...config, from: e.target.value as CarryForwardFrom })}
                className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
              >
                {FROM_OPTIONS.map(f => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
      </div>
    );
  }
);

IndicatorEditorCarryForward.displayName = 'IndicatorEditorCarryForward';

export default IndicatorEditorCarryForward;
